import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Dialog as MDialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  styled,
  Typography,
} from '@mui/material';
import SignatureCanvas from 'react-signature-canvas';
import { toast } from 'react-toastify';

import FHButton from '../FHButton';
import ClearSignatureIcon from '../../svgs/ClearSignatureIcon';
import CloseIcon from '../../svgs/CloseIcon';
import { getThemeColor } from '../../../utils/common';
import { Themes } from '../../../constants';
import { FORTA_BLACK, FORTA_BLUE, FORTA_GRAY4, FORTA_WHITE } from '../../../utils/colors';

import type { DialogProps } from '@mui/material';
import type { ReactNode } from 'react';

interface FHSignatureDialogProps extends DialogProps {
  cancelText?: string;
  headerText?: string;
  submitText?: string;
  consentText?: string;
  stepNumber?: number;
  colorTheme?: Themes;
  signing?: boolean;
  isSignatureDisabled?: boolean;
  contentRenderer?: () => ReactNode;
  onSign: (dataURL: string) => void;
}

const StyledDialog = styled(MDialog)<DialogProps>(() => ({
  '& .MuiDialog-paper': {
    boxSizing: 'border-box',
    width: 630,
    height: 'auto',
    maxWidth: 630,
    maxHeight: 'calc(100% - 64px)',
    backgroundColor: FORTA_WHITE,
    backgroundSize: '100%',
    boxShadow: '0px 4px 4px rgba(18, 78, 120, 0.25)',
    borderRadius: '10px',
    border: `2px solid ${FORTA_BLUE.light3}`,
    padding: '2em',
  },
  '& .MuiDialogContent-root': {
    padding: 0,
  },
  '& .MuiModal-backdrop': {
    backdropFilter: 'blur(2px)',
    backgroundColor: 'rgba(255, 255, 255, 0.4)',
  },
}));

function FHSignatureDialog(props: FHSignatureDialogProps) {
  const {
    open,
    cancelText,
    headerText,
    submitText,
    consentText = 'My electronic signature above is an accurate and complete representation of my digital signature. My signature is additionally on file with the company for additional verification, if needed.',
    stepNumber = 1,
    colorTheme = Themes.Primary,
    signing = false,
    isSignatureDisabled = false,
    contentRenderer,
    onClose,
    onSign,
    ...rest
  } = props;
  const [loading, setLoading] = useState<boolean>(false);
  const signRef = useRef<SignatureCanvas | null>(null);

  useEffect(() => {
    if (!stepNumber || !signRef.current) return;
    handleClear();
  }, [stepNumber, open]);

  useEffect(() => {
    if (!isSignatureDisabled) return;
    handleClear();
  }, [isSignatureDisabled]);

  const handleClear = () => {
    signRef.current?.clear();
  };

  const handleSubmit = async () => {
    if (isSignatureDisabled || !signRef.current) {
      return;
    }

    if (signRef.current.isEmpty()) {
      toast.error('A signature is required');
      return;
    }

    setLoading(true);
    const dataUrl = await signRef.current.getTrimmedCanvas().toDataURL('image/png');
    onSign(dataUrl);
    setLoading(false);
  };

  return (
    <StyledDialog open={open} {...rest} onClose={onClose}>
      <DialogTitle>
        {headerText && (
          <Box
            sx={{
              position: 'absolute',
              top: 15,
              left: 25,
              color: FORTA_BLUE.primary,
            }}
          >
            <Typography variant="h4" color={getThemeColor(colorTheme).primary}>
              {headerText}
            </Typography>
          </Box>
        )}
        <IconButton
          sx={{ p: 0, position: 'absolute', top: 24, right: 24 }}
          disableRipple
          onClick={(event) => onClose?.(event, 'backdropClick')}
        >
          <CloseIcon
            data-testid="closeSignatureDialog"
            color={getThemeColor(colorTheme).primary}
          />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {contentRenderer && contentRenderer()}
        <Paper
          sx={{
            position: 'relative',
            borderRadius: '10px',
            backgroundColor: FORTA_WHITE,
            m: '24px auto 0 auto',
            height: 180,
            overflow: 'hidden',
            cursor: isSignatureDisabled ? 'not-allowed' : 'default',
          }}
        >
          <SignatureCanvas
            ref={signRef}
            canvasProps={{
              className: 'sigCanvas',
              width: 602,
              height: 180,
              style: {
                pointerEvents: isSignatureDisabled ? 'none' : 'auto',
                opacity: isSignatureDisabled ? 0.5 : 1,
              },
            }}
            backgroundColor={getThemeColor(colorTheme).light3}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 20,
              left: 20,
              width: 'calc(100% - 40px)',
              height: '2px',
              opacity: isSignatureDisabled ? 0.5 : 1,
              backgroundColor: getThemeColor(colorTheme).primary,
            }}
          >
            <IconButton
              sx={{
                top: -35,
                left: -10,
              }}
              disabled={isSignatureDisabled}
              onClick={handleClear}
            >
              <ClearSignatureIcon
                data-testid="clearSignatureDialog"
                color={getThemeColor(colorTheme).primary}
              />
            </IconButton>
          </Box>
        </Paper>
        <Box
          sx={{
            marginTop: 2,
          }}
        >
          <Typography variant="body1" align="left" color={FORTA_BLACK} fontSize={12}>
            {consentText}
          </Typography>
        </Box>
        <DialogActions
          sx={{
            display: 'flex',
            justifyItems: 'end',
            width: '100%',
            boxSizing: 'border-box',
            p: '5px 0',
            mt: 2,
          }}
        >
          <FHButton
            data-testid="cancelSignatureDialog"
            disabled={loading || signing || isSignatureDisabled}
            onClick={handleClear}
            sx={{
              width: 'fit-content',
              backgroundColor: FORTA_WHITE,
              color: getThemeColor(colorTheme).primary,
              ':hover': { color: FORTA_GRAY4 },
              borderWidth: 1,
              borderStyle: 'solid',
              borderColor: FORTA_BLUE.light2,
            }}
            lightBackground={true}
          >
            {cancelText ? cancelText : 'Cancel'}
          </FHButton>
          <FHButton
            data-testid="submitSignatureDialog"
            disabled={loading || signing || isSignatureDisabled}
            colorTheme={colorTheme}
            loading={loading || signing}
            onClick={handleSubmit}
            sx={{ height: 40 }}
          >
            {submitText ? submitText : 'Submit'}
          </FHButton>
        </DialogActions>
      </DialogContent>
    </StyledDialog>
  );
}

export default FHSignatureDialog;
